/**
 * LineThreadState — durable per-thread memory that Line uses to avoid
 * re-asking anything the user has already said. Populated turn-by-turn
 * by a deterministic slot extractor and merged into the system prompt
 * as a KNOWN FACTS block so the model literally sees what is already known.
 */
import { parseNaturalDate, renderParsedDatesForPrompt, type ParsedDate } from './lineDateParser'

export const LINE_THREAD_STATE_VERSION = 1

export type LineThreadSlots = Partial<{
  outcome: string
  scope: string
  deadlineIso: string
  deadlineText: string
  timeframeText: string
  timeframeDays: number
  resources: string[]
  constraints: string[]
  audience: string
  style: string
  questionStyle: 'BATCH' | 'SINGLE'
}>

export type LineThreadConsents = Partial<{
  prepareProposal: boolean
  explainOnly: boolean
  detailedChecklist: boolean
  autoGenerateDates: boolean
  autoGenerateChecklists: boolean
  allowLineDefaults: boolean
}>

export interface LineThreadState {
  slots: LineThreadSlots
  consents: LineThreadConsents
  askedQuestions: string[]
  updatedAtIso: string
  version: number
}

export const emptyLineThreadState = (): LineThreadState => ({
  slots: {},
  consents: {},
  askedQuestions: [],
  updatedAtIso: new Date(0).toISOString(),
  version: LINE_THREAD_STATE_VERSION,
})

export const isLineThreadState = (value: unknown): value is LineThreadState => {
  if (!value || typeof value !== 'object') return false
  const record = value as Record<string, unknown>
  return (
    typeof record.updatedAtIso === 'string'
    && typeof record.version === 'number'
    && record.slots !== null && typeof record.slots === 'object'
    && record.consents !== null && typeof record.consents === 'object'
    && Array.isArray(record.askedQuestions)
  )
}

/** Merge new partial extraction into existing state; newer non-empty values win. */
export const mergeLineThreadState = (
  previous: LineThreadState,
  extracted: Partial<LineThreadState>,
): LineThreadState => {
  const mergedSlots: LineThreadSlots = { ...previous.slots }
  const nextSlots = extracted.slots ?? {}
  for (const key of Object.keys(nextSlots) as (keyof LineThreadSlots)[]) {
    const value = nextSlots[key]
    if (value === undefined || value === null) continue
    if (Array.isArray(value) && value.length === 0) continue
    if (typeof value === 'string' && value.trim() === '') continue
    if (Array.isArray(value)) {
      const prev = (mergedSlots[key] as unknown as string[] | undefined) ?? []
      const merged = Array.from(new Set([...prev, ...value.map((entry) => entry.trim()).filter(Boolean)]))
      ;(mergedSlots as Record<string, unknown>)[key] = merged
    } else {
      ;(mergedSlots as Record<string, unknown>)[key] = value
    }
  }
  const mergedConsents: LineThreadConsents = { ...previous.consents, ...(extracted.consents ?? {}) }
  const askedQuestions = Array.from(new Set([
    ...previous.askedQuestions,
    ...(extracted.askedQuestions ?? []),
  ])).slice(-25)
  return {
    slots: mergedSlots,
    consents: mergedConsents,
    askedQuestions,
    updatedAtIso: new Date().toISOString(),
    version: LINE_THREAD_STATE_VERSION,
  }
}

const TIMEFRAME_REGEX = /\b(\d+)[-\s]+(day|week|month|year)s?\b/i
const UNIT_TO_DAYS: Record<string, number> = { day: 1, week: 7, month: 30, year: 365 }
const TRAILING_JUNK_REGEX = /\s+(with\s+me|at\s+home|in\s+stock|on\s+hand|for\s+now|available)$/i

const ISO_DATE_REGEX = /\b(\d{4}-\d{2}-\d{2})\b/

const DEADLINE_TEXT_REGEX =
  /\b(by|before|due|deadline(?:\s+is)?|target(?:ing)?|complete\s+(?:by|before))\s+([A-Za-z0-9 ,-]{3,40}?)(?=[.,;!?]|$)/i

const RELATIVE_DAY_REGEX = /\b(today|tomorrow|day\s+after\s+tomorrow|next\s+week|next\s+month)\b/i

const DETAILED_CHECKLIST_REGEX =
  /\b(detailed|with\s+(details|recipe|instructions|descriptions|steps)|checklist(?:s)?\s+with|include\s+detailed)\b/i

const AUTO_DATES_REGEX =
  /\b(generate|auto[-\s]?generate|create)\s+(the\s+)?(dates?|schedule|timeline)\b|\bstandard\s+\d+[-\s]?(day|week|month)\s+(timeline|plan|schedule|sprint)\b|\b(you|line|ai)\s+(choose|decide|pick)\s+(the\s+)?(dates?|schedule|timeline)\b|\bchoose\s+(sensible\s+)?(dates?|schedule|timeline)\b/i

const AUTO_CHECKLIST_REGEX =
  /\b(generate|create|include)\s+(the\s+)?checklist(s)?\b|\bwith\s+detailed\s+checklist\b|\b(you|line|ai)\s+(choose|decide|pick)\s+(the\s+)?checklist(s)?\b|\bchoose\s+(sensible\s+)?checklist(s)?\b/i

const ALLOW_DEFAULTS_REGEX =
  /\b(you choose|you decide|decide for me|whatever you think|whichever you think|as you see fit|use sensible defaults|choose sensible defaults|pick what makes sense|line choose|ai choose|up to you)\b/i

const BATCH_QUESTION_REGEX =
  /\b(ask (?:me )?(?:all|everything)|all (?:the )?questions|in batches?|all at once|in one go|one go)\b/i

const SINGLE_QUESTION_REGEX =
  /\b(one question at a time|one by one|step by step)\b/i

const RESOURCE_REGEX =
  /\b(I\s+have|I\s+got|available:|resources:|ingredients:|with)\s+([A-Za-z0-9 ,'/-]{3,120}?)(?=[.]|$)/i

const CONSTRAINT_REGEXES: RegExp[] = [
  /\bunder\s+\$?\d[\d,]*(?:\.\d+)?\b/gi,
  /\bmust\s+not\s+[^.,;!?]{3,60}/gi,
  /\bmust\s+[^.,;!?]{3,60}/gi,
  /\bcannot\s+[^.,;!?]{3,60}/gi,
  /\bcan['’]t\s+[^.,;!?]{3,60}/gi,
  /\bonly\s+[^.,;!?]{3,60}/gi,
  /\bapproved?\s+by\s+[^.,;!?]{3,60}/gi,
  /\bneeds?\s+approval[^.,;!?]{0,40}/gi,
  /\bblocked\s+by\s+[^.,;!?]{3,60}/gi,
  /\bdepends?\s+on\s+[^.,;!?]{3,60}/gi,
]

const AUDIENCE_REGEX =
  /\bfor\s+(my|the|our)\s+([a-z][a-z' -]{2,40}?)(?=[.,;!?]|$)/i

const SCOPE_HINT_REGEXES: Array<[RegExp, string]> = [
  [/\bsurprise\s+(birthday|anniversary|engagement)?\s*party\b/i, 'surprise party'],
  [/\bbirthday\s+party\b/i, 'birthday party'],
  [/\bhome\s+loan\b/i, 'home loan'],
  [/\bwedding\b/i, 'wedding'],
  [/\bopening\s+webpage\b/i, 'opening webpage'],
  [/\blanding\s+page\b/i, 'landing page'],
  [/\bmarketing\s+launch\b/i, 'marketing launch'],
  [/\bproduct\s+launch\b/i, 'product launch'],
  [/\bfitness\s+plan\b/i, 'fitness plan'],
  [/\bcatering(?:\s+event)?\b/i, 'catering event'],
  [/\bwebsite\s+launch\b/i, 'website launch'],
]

const CONSENT_PROPOSAL_REGEX =
  /^(prepare|create|draft|make|build|generate|proceed|go\s+ahead|do\s+it|sounds\s+good|let'?s\s+(do|go|start)|okay|ok|yes|sure|yeah|yep|alright|please\s+(do|prepare|make|draft|generate|proceed))\b/i

const CONSENT_EXPLAIN_REGEX =
  /^(explain|explain\s+only|just\s+explain|only\s+explain|read[- ]?only|keep\s+it\s+read[- ]?only|no\s*[,!.]?\s*(just|only)?\s*explain)\b/i

const isShortReply = (text: string) => text.trim().length > 0 && text.trim().length <= 80

const unwrapStructuredUserText = (text: string) => {
  const trimmed = text.trim()
  if (!trimmed.startsWith('[')) return trimmed
  const clarificationValue = trimmed.match(/^value:\s*(.+)$/m)?.[1]?.trim()
  if (trimmed.startsWith('[LINE_CLARIFICATION_RESPONSE]') && clarificationValue) return clarificationValue
  if (trimmed.startsWith('[LINE_SUGGESTION_ACCEPTED]')) {
    const jsonStart = trimmed.indexOf('{')
    if (jsonStart >= 0) {
      try {
        const parsed = JSON.parse(trimmed.slice(jsonStart)) as { explicitResponse?: unknown }
        if (typeof parsed.explicitResponse === 'string' && parsed.explicitResponse.trim()) return parsed.explicitResponse.trim()
      } catch {
        return trimmed
      }
    }
  }
  return trimmed
}

const parseResourceList = (raw: string): string[] =>
  raw
    .split(/,|\band\b/i)
    .map((piece) => piece.trim()
      .replace(/^some\s+|\s+etc\.?$/gi, '')
      .replace(TRAILING_JUNK_REGEX, '')
      .trim())
    .filter((piece) => piece.length >= 2 && piece.length <= 40 && !/^(me|us|it|them|this|that)$/i.test(piece))
    .slice(0, 8)

const extractConstraints = (text: string): string[] => {
  const matches = CONSTRAINT_REGEXES.flatMap((regex) => Array.from(text.matchAll(regex), (match) => match[0]?.trim() ?? ''))
  return Array.from(new Set(matches.map((entry) => entry.replace(/\s+/g, ' ').trim()).filter(Boolean))).slice(0, 8)
}

const extractScopeHint = (text: string): string | undefined => {
  for (const [regex, label] of SCOPE_HINT_REGEXES) {
    if (regex.test(text)) return label
  }
  return undefined
}

const extractTimeframe = (text: string) => {
  const match = TIMEFRAME_REGEX.exec(text)
  if (!match) return null
  const value = Number(match[1])
  if (!Number.isFinite(value) || value <= 0) return null
  const unit = match[2].toLowerCase()
  return { text: match[0], days: value * (UNIT_TO_DAYS[unit] ?? 1) }
}

const extractDeadline = (text: string) => {
  const iso = ISO_DATE_REGEX.exec(text)
  if (iso) return { iso: iso[1], text: iso[1] }
  const dm = DEADLINE_TEXT_REGEX.exec(text)
  if (dm) return { iso: undefined, text: `${dm[1]} ${dm[2]}`.trim() }
  const rel = RELATIVE_DAY_REGEX.exec(text)
  if (rel) return { iso: undefined, text: rel[1] }
  return null
}

/**
 * Extract slots + consents from a single user turn.
 * Deterministic, zero-latency, high-precision. Returns only fields we're
 * confident about; the merge step never overwrites with empty values.
 */
export const extractSlotsFromUserTurn = (userText: string, now: Date = new Date()): Partial<LineThreadState> => {
  const trimmed = unwrapStructuredUserText(userText)
  if (!trimmed) return { slots: {}, consents: {} }

  const slots: LineThreadSlots = {}
  const consents: LineThreadConsents = {}

  const timeframe = extractTimeframe(trimmed)
  if (timeframe) {
    slots.timeframeText = timeframe.text
    slots.timeframeDays = timeframe.days
  }

  const parsedDate: ParsedDate | null = parseNaturalDate(trimmed, now)
  if (parsedDate) {
    slots.deadlineIso = parsedDate.iso
    slots.deadlineText = parsedDate.sourceText
  } else {
    const deadline = extractDeadline(trimmed)
    if (deadline) {
      if (deadline.iso) slots.deadlineIso = deadline.iso
      if (deadline.text) slots.deadlineText = deadline.text
    }
  }

  const scope = extractScopeHint(trimmed)
  if (scope) slots.scope = scope

  const resource = RESOURCE_REGEX.exec(trimmed)
  if (resource) {
    const items = parseResourceList(resource[2])
    if (items.length > 0) slots.resources = items
  }

  const constraints = extractConstraints(trimmed)
  if (constraints.length > 0) slots.constraints = constraints

  const audience = AUDIENCE_REGEX.exec(trimmed)
  if (audience) {
    const label = audience[2].trim().toLowerCase()
    if (label.length > 0) slots.audience = `${audience[1]} ${label}`.trim()
  }

  if (DETAILED_CHECKLIST_REGEX.test(trimmed)) consents.detailedChecklist = true
  if (AUTO_DATES_REGEX.test(trimmed)) consents.autoGenerateDates = true
  if (AUTO_CHECKLIST_REGEX.test(trimmed)) consents.autoGenerateChecklists = true
  if (ALLOW_DEFAULTS_REGEX.test(trimmed)) consents.allowLineDefaults = true
  if (BATCH_QUESTION_REGEX.test(trimmed)) slots.questionStyle = 'BATCH'
  if (SINGLE_QUESTION_REGEX.test(trimmed)) slots.questionStyle = 'SINGLE'

  if (isShortReply(trimmed)) {
    if (CONSENT_EXPLAIN_REGEX.test(trimmed)) consents.explainOnly = true
    else if (CONSENT_PROPOSAL_REGEX.test(trimmed)) consents.prepareProposal = true
  }

  return { slots, consents }
}

/**
 * Rebuild state from an entire chat thread. Used when hydrating a
 * persisted session that has no stored state (backfill path).
 */
export const rebuildLineThreadStateFromTurns = (
  turns: Array<{ role: string; text: string; requestText?: string }>,
): LineThreadState => {
  let state = emptyLineThreadState()
  for (const turn of turns) {
    const raw = turn.requestText ?? turn.text
    if (turn.role !== 'USER' || !raw) continue
    state = mergeLineThreadState(state, extractSlotsFromUserTurn(raw))
  }
  return state
}

/**
 * Render the state as a KNOWN FACTS block for the system prompt.
 * Returns an empty string when there's nothing worth saying so the block
 * degrades cleanly for a brand-new thread.
 */
export const renderLineThreadStateForPrompt = (
  state: LineThreadState | undefined | null,
  parsedDate?: ParsedDate | null,
): string => {
  if (!state) return parsedDate ? renderParsedDatesForPrompt(parsedDate) : ''
  const lines: string[] = []
  const { slots, consents } = state
  if (slots.scope) lines.push(`- Scope: ${slots.scope}`)
  if (slots.outcome) lines.push(`- Outcome: ${slots.outcome}`)
  if (slots.audience) lines.push(`- Audience: ${slots.audience}`)
  if (slots.timeframeText) {
    const daysPart = typeof slots.timeframeDays === 'number' ? ` (~${slots.timeframeDays} days)` : ''
    lines.push(`- Timeframe: ${slots.timeframeText}${daysPart}`)
  }
  if (slots.deadlineText || slots.deadlineIso) {
    lines.push(`- Deadline: ${slots.deadlineIso ?? slots.deadlineText}`)
  }
  if (slots.resources && slots.resources.length > 0) lines.push(`- Resources on hand: ${slots.resources.join(', ')}`)
  if (slots.constraints && slots.constraints.length > 0) lines.push(`- Constraints: ${slots.constraints.join('; ')}`)
  if (slots.style) lines.push(`- Style preference: ${slots.style}`)
  if (slots.questionStyle === 'BATCH') lines.push('- Question style: ask for missing details in batches')
  if (slots.questionStyle === 'SINGLE') lines.push('- Question style: ask one question at a time')
  const consentLines: string[] = []
  if (consents.prepareProposal) consentLines.push('user has agreed to prepare a Manage-ready draft')
  if (consents.explainOnly) consentLines.push('user asked for explanation only (no draft)')
  if (consents.detailedChecklist) consentLines.push('user wants detailed checklist descriptions')
  if (consents.autoGenerateDates) consentLines.push('user authorized auto-generating dates')
  if (consents.autoGenerateChecklists) consentLines.push('user authorized generating checklists')
  if (consents.allowLineDefaults) consentLines.push('user authorized Line to choose sensible defaults for missing details')
  if (consentLines.length > 0) lines.push(`- Decisions locked in: ${consentLines.join('; ')}`)
  const parsedLine = renderParsedDatesForPrompt(parsedDate)
  const factsBlock = lines.length > 0
    ? `KNOWN FACTS (already answered — never re-ask these):\n${lines.join('\n')}`
    : ''
  return [factsBlock, parsedLine].filter(Boolean).join('\n\n')
}
